"use client";

import { useRef, useState } from "react";
import { Input } from "@/components/ui";
import { NEWS_PORTAL_PATH } from "@/lib/constants";
import { SearchIcon } from "lucide-react";

type SearchCardProps = {
	variant?: "default" | "expandable";
	className?: string;
};

const SearchCard = ({ variant = "default", className }: SearchCardProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const formRef = useRef<HTMLFormElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const isExpandable = variant === "expandable";

	const value = () => inputRef.current?.value.trim() ?? "";

	const handleClick = () => {
		if (!isExpandable) return;
		if (!isOpen) return setIsOpen(true);
		if (!value()) return setIsOpen(false);
		if (formRef.current?.requestSubmit) {
			formRef.current.requestSubmit();
		} else {
			formRef.current?.submit();
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		if (isExpandable && !value()) {
			e.preventDefault();
			setIsOpen(false);
		}
	};

	return (
		<form action={NEWS_PORTAL_PATH} ref={formRef} onSubmit={handleSubmit}>
			<div className={`flex items-center gap-2 ${className}`}>
				<div
					className={`transition-all duration-300 ${
						isExpandable ? (isOpen ? "w-56" : "w-0 overflow-hidden") : "w-full"
					}`}
				>
					<Input
						ref={inputRef}
						name="s"
						type="text"
						placeholder="Search..."
						classNames={{
							label: "m-0",
							input: `px-4 py-2 border rounded-md outline-none ${
								isExpandable && !isOpen ? "pointer-events-none opacity-0" : ""
							}`,
						}}
					/>
				</div>

				<button
					type={isExpandable ? "button" : "submit"}
					onClick={isExpandable ? handleClick : undefined}
					aria-label={isExpandable ? (isOpen ? "Collapse search" : "Expand search") : "Search"}
					aria-expanded={isExpandable ? isOpen : undefined}
					className="flex shrink-0 items-center rounded bg-brand-primary p-2 text-white"
				>
					<SearchIcon />
				</button>
			</div>
		</form>
	);
};

export default SearchCard;
